export const creativeProblemSolvingModule = {
  id: "ips-creative-problem-solving",
  title: "Creative Problem-Solving Techniques",
  description: "Master systematic approaches to generate innovative solutions and break through mental barriers.",
  content: `
## What You'll Learn
By the end of this module, you will be able to:
- Apply divergent and convergent thinking techniques to expand solution possibilities
- Use systematic creativity methods like SCAMPER and Six Thinking Hats
- Break through mental blocks and assumptions that limit innovative thinking
- Evaluate and select the most promising solutions using structured decision frameworks

## Prerequisites
- Experience with basic problem-solving in professional or academic contexts
- Understanding of design thinking fundamentals (previous module recommended)

## Overview
Creative problem-solving combines structured methodologies with innovative thinking to tackle challenges that don't have obvious solutions. Organizations that train employees in creative problem-solving report 67% more breakthrough innovations and 45% faster time-to-solution for complex challenges.

---

## Key Concepts

### Divergent vs. Convergent Thinking
Creative problem-solving alternates between divergent thinking (generating many possibilities) and convergent thinking (evaluating and selecting the best options). This rhythm prevents premature judgment while ensuring practical outcomes.

#### Why It Matters
Teams that separate idea generation from idea evaluation generate 3x more innovative solutions and make decisions 50% faster. Mixing these thinking modes reduces both creativity and decision quality.

#### Best Practices
- Use timeboxed divergent sessions to generate ideas without criticism
- Apply structured convergent methods to evaluate options fairly
- Defer judgment during brainstorming to encourage wild ideas
- Use criteria-based evaluation to select solutions objectively

### Systematic Creativity Techniques
While creativity might seem random, systematic techniques consistently generate more innovative solutions than unstructured brainstorming. These methods help you think in new directions deliberately.

#### Industry Usage
Innovation consultancies like IDEO and companies like 3M use systematic creativity techniques because they produce 40% more actionable ideas than traditional brainstorming and reduce the time needed to reach breakthrough solutions.

---

## Practical Examples

### Example 1: SCAMPER Technique Application
Use systematic prompts to transform existing solutions into innovative alternatives.

**SCAMPER Framework:**
- **S**ubstitute: What can be substituted or switched?
- **C**ombine: What can be combined or merged?
- **A**dapt: What can be adapted from elsewhere?
- **M**odify: What can be magnified, minimized, or changed?
- **P**ut to other uses: How else can this be used?
- **E**liminate: What can be removed or simplified?
- **R**everse: What can be rearranged or reversed?

**Example**: Improving team meetings using SCAMPER
- Substitute: Replace status updates with async video messages
- Combine: Merge decision-making with walking meetings
- Adapt: Use gaming elements from video games for engagement

### Example 2: Six Thinking Hats Decision Framework
Evaluate solutions from multiple perspectives systematically.

**Hat Colors and Perspectives:**
- **White**: Facts, data, information needs
- **Red**: Emotions, feelings, intuition
- **Black**: Caution, critical thinking, risks
- **Yellow**: Optimism, benefits, positive outcomes
- **Green**: Creativity, alternatives, new ideas
- **Blue**: Process, control, meta-thinking

💡 **Pro Tip**: Use the "Yes, and..." principle during divergent thinking - build on ideas rather than rejecting them, even if they seem impractical initially.

---

## Industry Adoption

### Why Companies Choose This Approach
Organizations invest in creative problem-solving training because it improves innovation rates by 65% and reduces problem-solving time by 35%. These techniques work across departments - from engineering to marketing to operations.

### Popular Tools and Libraries
- **MindMeister**: Mind mapping for visual idea organization
- **IdeaFlip**: Digital sticky notes for remote brainstorming sessions
- **Crazy 8s**: Rapid sketching technique for generating visual solutions

### Comparison with Alternatives
| Approach | Advantages | Use Cases |
|----------|------------|-----------|
| Systematic Creativity | Reproducible, comprehensive, efficient | Complex problems, team environments, time constraints |
| Intuitive Problem-Solving | Natural, fast, personal insight | Individual work, familiar domains, time pressure |

---

## Key Takeaways

✅ **Essential Points**:
- Separating divergent and convergent thinking improves both creativity and decision quality
- Systematic creativity techniques generate more innovative solutions than unstructured approaches
- Mental blocks can be overcome through perspective shifts and systematic exploration
- Structured evaluation frameworks ensure practical solutions emerge from creative sessions

⚠️ **Common Pitfalls**:
- Judging ideas too quickly during brainstorming, which kills creative momentum
- Generating many ideas without systematic evaluation, leading to decision paralysis

## Next Steps
You'll learn innovation frameworks that help you implement creative solutions systematically within organizational contexts.

---
`,
  duration: "50 minutes",
  difficulty: "intermediate" as const,
  objectives: [
    "Master divergent/convergent thinking",
    "Apply systematic creativity techniques",
    "Break through mental barriers",
    "Use structured solution evaluation"
  ],
  keyTopics: [
    "Divergent Thinking",
    "Convergent Thinking",
    "SCAMPER Technique",
    "Six Thinking Hats",
    "Systematic Creativity"
  ]
};